import { previewDescriptor } from '@/utils/messagePresentation.mjs';
import { defineStore } from 'pinia';
import { imClient } from '@/services/imClient';
import { appConfig } from '@/config';
import { useSessionStore } from '@/stores/session';
import { useLanguageStore } from '@/stores/language';
import { collapseSenderRecallDuplicates } from '@/utils/messageRecall.mjs';

const LOCAL_CONVERSATION_STORAGE_KEY = 'qmt-web-im-local-conversations-v2';
const LOCAL_FAVORITE_MESSAGES_KEY = 'qmt-web-im-favorite-messages-v1';

function scopedStorageKey(baseKey) {
  const session = useSessionStore();
  const scope = `${appConfig.appServer || ''}:${session.userId || 'anonymous'}`;
  return `${baseKey}:${encodeURIComponent(scope)}`;
}

function readFavoriteMessages() {
  try {
    return JSON.parse(localStorage.getItem(scopedStorageKey(LOCAL_FAVORITE_MESSAGES_KEY)) || '[]');
  } catch (error) {
    return [];
  }
}

function writeFavoriteMessages(messages) {
  localStorage.setItem(scopedStorageKey(LOCAL_FAVORITE_MESSAGES_KEY), JSON.stringify(messages));
}

function readLocalConversations() {
  try {
    return JSON.parse(localStorage.getItem(scopedStorageKey(LOCAL_CONVERSATION_STORAGE_KEY)) || '[]');
  } catch (error) {
    return [];
  }
}

function writeLocalConversations(conversations) {
  localStorage.setItem(scopedStorageKey(LOCAL_CONVERSATION_STORAGE_KEY), JSON.stringify(conversations));
}

function randomId() {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID().replaceAll('-', '');
  }
  return `${Date.now()}${Math.random().toString(16).slice(2)}`;
}

function messageKey(message) {
  return message.id || message.messageUid;
}

function messageStableKey(message) {
  return String(message?.messageUid || message?.id || '');
}

function messageActivityTimestamp(message) {
  return Number(message?.activityTimestamp || message?.editedAt || message?.displayTimestamp || message?.timestamp || 0);
}

function isMessageActionExpired(message, limitMinutes) {
  const limit = Math.max(0, Number(limitMinutes) || 0);
  if (limit <= 0) {
    return false;
  }
  const sentAt = Number(message?.displayTimestamp || message?.timestamp || message?.serverTimestamp || 0);
  return sentAt > 0 && Date.now() - sentAt > limit * 60 * 1000;
}

function groupIdFromConversation(conversation) {
  if (!conversation || !['group', 'channel'].includes(conversation.type)) {
    return '';
  }
  return String(conversation.target || conversation.raw?.groupID || conversation.raw?.target || '');
}

function isRecalledMessage(message) {
  return Boolean(message?.recalled) || message?.status === 'recalled';
}

function attachmentLabel(message) {
  const attachment = message?.attachment;
  if (!attachment) {
    return '';
  }
  if (attachment.type === 'image') {
    return '[图片]';
  }
  if (attachment.type === 'voice') {
    return '[语音]';
  }
  return `[文件] ${attachment.name || '文件'}`;
}

function messagePreviewText(message) {
  if (isRecalledMessage(message) && message?.direction !== 'out') {
    return '';
  }
  const text = String(message?.text || '').replace(/\s+/g, ' ').trim();
  return text || attachmentLabel(message);
}

function quotePrefix(message) {
  const text = messagePreviewText(message);
  return text.length > 48 ? `${text.slice(0, 48)}...` : text;
}

async function writeClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const element = document.createElement('textarea');
  element.value = text;
  element.setAttribute('readonly', 'readonly');
  element.style.position = 'fixed';
  element.style.left = '-9999px';
  document.body.appendChild(element);
  element.select();
  document.execCommand('copy');
  document.body.removeChild(element);
}

function isSameMessage(left, right) {
  if (left.messageUid && right.messageUid && left.messageUid === right.messageUid) {
    return true;
  }
  if (left.id && right.id && left.id === right.id) {
    return true;
  }
  return left.direction === right.direction
    && left.text === right.text
    && (left.type || 'text') === (right.type || 'text')
    && Math.abs(Number(left.displayTimestamp || left.timestamp || 0) - Number(right.displayTimestamp || right.timestamp || 0)) < 15000;
}

function hasExactMessage(messages, message) {
  return messages.some(item => {
    if (item.id && message.id && item.id === message.id) {
      return true;
    }
    return item.messageUid && message.messageUid && item.messageUid === message.messageUid;
  });
}

function shouldUseMessagePolling() {
  return imClient.shouldUseMessagePolling ? imClient.shouldUseMessagePolling() : true;
}

function canMarkReadNow(options = {}) {
  if (typeof document === 'undefined') {
    return false;
  }
  if (document.visibilityState !== 'visible') {
    return false;
  }
  if (options.userAction) {
    return true;
  }
  return true;
}

function mergeVisibleMessages(currentMessages, syncedMessages) {
  const merged = [...syncedMessages];
  currentMessages.forEach(currentMessage => {
    const exists = merged.some(message => isSameMessage(message, currentMessage));
    const shouldKeepLocal = currentMessage.status === 'sending'
      || currentMessage.status === 'failed'
      || (currentMessage.direction === 'out'
        && currentMessage.status === 'sent'
        && Date.now() - Number(currentMessage.timestamp || 0) < 30000);
    if (!exists && shouldKeepLocal) {
      merged.push(currentMessage);
    }
  });

  const unique = merged
    .filter((message, index, messages) => {
      const key = messageKey(message);
      return messages.findIndex(item => messageKey(item) === key) === index;
    });
  return collapseSenderRecallDuplicates(unique)
    .sort((a, b) => Number(a.displayTimestamp || a.timestamp || 0) - Number(b.displayTimestamp || b.timestamp || 0));
}

function incomingEditedMessages(previousMessages, nextMessages) {
  const previousByKey = new Map();
  (previousMessages || []).forEach(message => {
    const key = messageStableKey(message);
    if (key) {
      previousByKey.set(key, message);
    }
  });

  return (nextMessages || []).filter(message => {
    if (message.direction !== 'in') {
      return false;
    }
    const editedAt = Number(message.editedAt || 0);
    if (editedAt <= 0) {
      return false;
    }
    const previous = previousByKey.get(messageStableKey(message));
    return previous && editedAt > Number(previous.editedAt || 0);
  });
}

let notificationAudioContext = null;

function playNotificationSound() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
      return;
    }
    notificationAudioContext = notificationAudioContext || new AudioContext();
    if (notificationAudioContext.state === 'suspended') {
      notificationAudioContext.resume().catch(() => {});
    }
    const oscillator = notificationAudioContext.createOscillator();
    const gain = notificationAudioContext.createGain();
    const startAt = notificationAudioContext.currentTime;
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, startAt);
    oscillator.frequency.setValueAtTime(660, startAt + 0.1);
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(0.12, startAt + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.22);
    oscillator.connect(gain);
    gain.connect(notificationAudioContext.destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + 0.24);
  } catch (error) {
    // Notification sound is best-effort; browsers may block audio until user interaction.
  }
}

export const useConversationStore = defineStore('conversations', {
  state: () => ({
    activeId: '',
    conversations: [],
    messagesByConversation: {},
    visibleMessagesByConversation: {},
    eventsBound: false,
    usingDemoData: false,
    loading: false,
    syncTimer: null,
    conversationSyncTimer: null,
    messageActionSettingsTimer: null,
    readPresenceEventsBound: false,
    syncInFlight: false,
    pendingReadReceiptConversationId: '',
    conversationSyncInFlight: false,
    syncAfterSendTimer: null,
    optimisticMessagesByConversation: {},
    quotedMessage: null,
    forwardingMessages: [],
    editingMessage: null,
    editingText: '',
    editingMode: '',
    editingBusy: false,
    editingError: '',
    messageDetailOpen: false,
    messageDetailLoading: false,
    messageDetailErrorKey: '',
    messageDetail: null,
    attentionMessageId: '',
    attentionMessageTimer: null,
    selectionMode: false,
    selectedMessageIds: [],
    favoriteMessages: [],
    activeFavoriteKey: '',
    actionNotice: '',
    actionError: '',
    recallLimitMinutes: 0,
    editLimitMinutes: 0,
    groupMessageActionSettings: {}
  }),
  getters: {
    activeConversation(state) {
      return state.conversations.find(item => item.id === state.activeId) || state.conversations[0] || null;
    },
    activeMessages(state) {
      return state.visibleMessagesByConversation[state.activeId] || [];
    },
    totalUnread(state) {
      return state.conversations.reduce((sum, item) => sum + Number(item.unread || 0), 0);
    },
    canSendActiveConversation(state) {
      const conversation = state.conversations.find(item => item.id === state.activeId) || state.conversations[0] || null;
      if (!conversation) {
        return false;
      }
      if (conversation.type !== 'channel') {
        return true;
      }
      return ['owner', 'admin'].includes(conversation.role || conversation.raw?.role || '');
    },
    favoriteMessageIds(state) {
      return state.favoriteMessages.map(message => message.key);
    },
    activeFavorite(state) {
      return state.favoriteMessages.find(message => message.key === state.activeFavoriteKey)
        || state.favoriteMessages[0]
        || null;
    },
    activeRecallLimitMinutes(state) {
      const conversation = state.conversations.find(item => item.id === state.activeId) || state.conversations[0] || null;
      const groupId = groupIdFromConversation(conversation);
      const settings = groupId ? state.groupMessageActionSettings[groupId] : null;
      return settings?.configured ? Number(settings.recallLimitMinutes || 0) : Number(state.recallLimitMinutes || 0);
    },
    activeEditLimitMinutes(state) {
      const conversation = state.conversations.find(item => item.id === state.activeId) || state.conversations[0] || null;
      const groupId = groupIdFromConversation(conversation);
      const settings = groupId ? state.groupMessageActionSettings[groupId] : null;
      return settings?.configured ? Number(settings.editLimitMinutes || 0) : Number(state.editLimitMinutes || 0);
    },
    selectedMessages(state) {
      const selectedIds = new Set(state.selectedMessageIds);
      return (state.visibleMessagesByConversation[state.activeId] || [])
        .filter(message => selectedIds.has(messageStableKey(message)));
    }
  },
  actions: {
    bootstrapDemoData() {
      const session = useSessionStore();
      this.bindImEvents();
      this.usingDemoData = false;
      if (!session.userId) {
        this.clearUserState();
        return;
      }
      this.loadScopedLocalState();
      this.refreshMessageActionSettings();
      this.refreshActiveGroupMessageActionSettings();
      this.startMessageSync();
    },
    loadScopedLocalState() {
      this.conversations = readLocalConversations();
      this.favoriteMessages = readFavoriteMessages();
      this.activeFavoriteKey = this.favoriteMessages[0]?.key || '';
      this.activeId = this.conversations[0]?.id || '';
      this.messagesByConversation = {};
      this.visibleMessagesByConversation = {};
      this.optimisticMessagesByConversation = {};
      this.quotedMessage = null;
      this.forwardingMessages = [];
      this.editingMessage = null;
      this.editingText = '';
      this.editingMode = '';
      this.editingBusy = false;
      this.editingError = '';
      this.messageDetailOpen = false;
      this.messageDetailLoading = false;
      this.messageDetailErrorKey = '';
      this.messageDetail = null;
      this.selectionMode = false;
      this.selectedMessageIds = [];
      if (this.activeId) {
        this.loadActiveMessages({ allowReadReceipt: true });
      }
    },
    clearUserState() {
      window.clearInterval(this.syncTimer);
      window.clearInterval(this.conversationSyncTimer);
      window.clearInterval(this.messageActionSettingsTimer);
      window.clearTimeout(this.syncAfterSendTimer);
      window.clearTimeout(this.attentionMessageTimer);
      this.syncTimer = null;
      this.conversationSyncTimer = null;
      this.messageActionSettingsTimer = null;
      this.syncAfterSendTimer = null;
      this.attentionMessageTimer = null;
      this.syncInFlight = false;
      this.conversationSyncInFlight = false;
      this.pendingReadReceiptConversationId = '';
      this.activeId = '';
      this.conversations = [];
      this.messagesByConversation = {};
      this.visibleMessagesByConversation = {};
      this.optimisticMessagesByConversation = {};
      this.quotedMessage = null;
      this.forwardingMessages = [];
      this.editingMessage = null;
      this.editingText = '';
      this.editingMode = '';
      this.editingBusy = false;
      this.editingError = '';
      this.messageDetailOpen = false;
      this.messageDetailLoading = false;
      this.messageDetailErrorKey = '';
      this.messageDetail = null;
      this.attentionMessageId = '';
      this.selectionMode = false;
      this.selectedMessageIds = [];
      this.favoriteMessages = [];
      this.activeFavoriteKey = '';
      this.actionNotice = '';
      this.actionError = '';
      this.recallLimitMinutes = 0;
      this.editLimitMinutes = 0;
      this.groupMessageActionSettings = {};
    },
    bindImEvents() {
      if (this.eventsBound) {
        return;
      }
      this.eventsBound = true;
      imClient.on('messageSent', message => {
        this.appendMessage(message.conversationId, message);
      });
      imClient.on('messageReceived', message => {
        this.appendMessage(message.conversationId, message);
        if (message.conversationId === this.activeId) {
          this.syncActiveMessages({ allowReadReceipt: true });
        }
      });
      imClient.on('messageStatusChanged', message => {
        this.replaceMessage(message.conversationId, message);
        if (isRecalledMessage(message) && message.direction === 'out') {
          const conversation = this.conversations.find(item => item.id === message.conversationId);
          if (conversation) {
            conversation.subtitle = messagePreviewText(message);
            conversation.previewMessage = previewDescriptor(message);
            this.conversations = [...this.conversations];
            writeLocalConversations(this.conversations);
          }
        }
      });
      imClient.on('messageHidden', message => {
        this.removeMessages(message.conversationId, [message]);
        const conversation = this.conversations.find(item => item.id === message.conversationId);
        if (conversation) {
          const latest = (this.messagesByConversation[message.conversationId] || [])
            .reduce((current, item) => !current || messageActivityTimestamp(item) > messageActivityTimestamp(current) ? item : current, null);
          conversation.subtitle = latest ? messagePreviewText(latest) : '';
          conversation.previewMessage = previewDescriptor(latest);
          conversation.updatedAt = latest ? messageActivityTimestamp(latest) : conversation.updatedAt;
          this.conversations = [...this.conversations];
          writeLocalConversations(this.conversations);
        }
      });
      imClient.on('messageEdited', message => {
        this.replaceMessage(message.conversationId, message);
        const conversation = this.conversations.find(item => item.id === message.conversationId);
        if (conversation) {
          conversation.subtitle = messagePreviewText(message);
          conversation.previewMessage = previewDescriptor(message);
          conversation.updatedAt = messageActivityTimestamp(message);
          this.conversations = [...this.conversations]
            .sort((left, right) => Number(right.updatedAt || 0) - Number(left.updatedAt || 0));
          writeLocalConversations(this.conversations);
        }
        if (message.direction === 'in' && message.conversationId === this.activeId) {
          this.focusMessage(message);
          playNotificationSound();
        }
      });
      imClient.on('messageEditFailed', ({ conversationId, originalMessage, editedMessage, error }) => {
        this.replaceLocalMessage(conversationId, editedMessage, originalMessage);
        if (conversationId === this.activeId) {
          this.setActionError(error || '编辑消息发送失败，已恢复原内容');
        }
      });
      imClient.on('conversationsChanged', remoteConversations => {
        this.usingDemoData = false;
        this.applyRemoteConversations(remoteConversations);
      });
      imClient.on('groupInfoChanged', () => {
        this.refreshActiveGroupMessageActionSettings();
      });
    },
    setActiveConversation(id) {
      if (this.activeId !== id) {
        this.cancelEditMessage();
        this.clearActionFeedback();
      }
      this.activeId = id;
      const conversation = this.conversations.find(item => item.id === id);
      if (conversation) {
        conversation.unread = 0;
      }
      this.loadActiveMessages({ allowReadReceipt: true, userAction: true });
      this.refreshActiveGroupMessageActionSettings();
    },
    async refreshMessageActionSettings() {
      try {
        const settings = await imClient.getMessageActionSettings();
        this.recallLimitMinutes = settings.recallLimitMinutes;
        this.editLimitMinutes = settings.editLimitMinutes;
      } catch (error) {
        // Keep the last known limits if the business settings endpoint is temporarily unavailable.
      }
    },
    setGroupMessageActionSettings(groupId, settings) {
      const key = String(groupId || '');
      if (!key) {
        return;
      }
      this.groupMessageActionSettings = {
        ...this.groupMessageActionSettings,
        [key]: {
          configured: Boolean(settings?.configured),
          recallLimitMinutes: Math.max(0, Number(settings?.recallLimitMinutes) || 0),
          editLimitMinutes: Math.max(0, Number(settings?.editLimitMinutes) || 0)
        }
      };
    },
    async refreshActiveGroupMessageActionSettings() {
      const groupId = groupIdFromConversation(this.activeConversation);
      if (!groupId) {
        return;
      }
      try {
        const settings = await imClient.getGroupMessageActionSettings(groupId);
        this.setGroupMessageActionSettings(groupId, settings);
      } catch (error) {
        // Continue using the global policy when the group extension cannot be read temporarily.
      }
    },
    sendText(text) {
      const conversation = this.activeConversation;
      if (!conversation || !text.trim()) {
        return;
      }
      let value = text.trim();
      if (this.quotedMessage) {
        value = `引用：${quotePrefix(this.quotedMessage)}\n${value}`;
        this.quotedMessage = null;
      }
      const message = {
        id: randomId(),
        conversationId: conversation.id,
        from: 'me',
        direction: 'out',
        type: 'text',
        text: value,
        status: 'sending',
        deliveryStatus: 'sending',
        timestamp: Date.now(),
        displayTimestamp: Date.now()
      };
      this.appendVisibleMessage(conversation.id, message);
      this.appendMessage(conversation.id, message);
      imClient.sendTextMessage(conversation, value, message);
      conversation.subtitle = value;
      conversation.previewMessage = previewDescriptor(message);
      conversation.updatedAt = Date.now();
      writeLocalConversations(this.conversations);
      window.clearTimeout(this.syncAfterSendTimer);
      this.syncAfterSendTimer = window.setTimeout(() => this.syncActiveMessages(), 250);
    },
    async retryMessage(message) {
      const conversation = this.conversations.find(item => item.id === message?.conversationId) || this.activeConversation;
      if (!conversation || message?.status !== 'failed' || message?.type !== 'text' || !String(message?.text || '').trim()) {
        return;
      }
      const pending = {
        ...message,
        status: 'sending',
        deliveryStatus: 'sending',
        error: ''
      };
      this.replaceLocalMessage(conversation.id, message, pending);
      const sent = await imClient.sendTextMessage(conversation, pending.text, pending);
      this.replaceMessage(conversation.id, sent);
    },
    sendAttachment({ type, file, text = '' }) {
      const conversation = this.activeConversation;
      if (!conversation || !file || !['image', 'file'].includes(type)) {
        return;
      }
      let value = text.trim();
      if (this.quotedMessage) {
        value = `引用：${quotePrefix(this.quotedMessage)}\n${value}`.trim();
        this.quotedMessage = null;
      }
      const now = Date.now();
      const message = {
        id: randomId(),
        conversationId: conversation.id,
        from: 'me',
        senderRole: conversation.role || conversation.raw?.role || '',
        direction: 'out',
        type,
        text: value,
        attachment: {
          type,
          name: file.name || (type === 'image' ? '图片' : '文件'),
          mime: file.type || '',
          size: file.size || 0,
          url: type === 'image' ? URL.createObjectURL(file) : ''
        },
        status: 'sending',
        deliveryStatus: 'sending',
        timestamp: now,
        displayTimestamp: now
      };
      this.appendVisibleMessage(conversation.id, message);
      this.appendMessage(conversation.id, message);
      imClient.sendAttachmentMessage(conversation, { type, file, text: value }, message);
      conversation.subtitle = messagePreviewText(message);
      conversation.previewMessage = previewDescriptor(message);
      conversation.updatedAt = now;
      writeLocalConversations(this.conversations);
      window.clearTimeout(this.syncAfterSendTimer);
      this.syncAfterSendTimer = window.setTimeout(() => this.syncActiveMessages(), 350);
    },
    sendVoice({ file, duration }) {
      const conversation = this.activeConversation;
      if (!conversation || !file || !Number.isFinite(Number(duration)) || Number(duration) < 1) return;
      const now = Date.now();
      const sourceLanguage = useLanguageStore().current;
      const message = {
        id: randomId(),
        conversationId: conversation.id,
        from: 'me',
        senderRole: conversation.role || conversation.raw?.role || '',
        direction: 'out',
        type: 'voice',
        text: '',
        attachment: {
          type: 'voice',
          name: '语音消息',
          mime: file.type || '',
          size: file.size || 0,
          duration: Number(duration),
          sourceLanguage,
          url: URL.createObjectURL(file)
        },
        status: 'sending',
        deliveryStatus: 'sending',
        timestamp: now,
        displayTimestamp: now
      };
      this.appendVisibleMessage(conversation.id, message);
      this.appendMessage(conversation.id, message);
      imClient.sendVoiceMessage(conversation, { file, duration: Number(duration), sourceLanguage }, message);
      conversation.subtitle = '[语音]';
      conversation.previewMessage = previewDescriptor(message);
      conversation.updatedAt = now;
      writeLocalConversations(this.conversations);
      window.clearTimeout(this.syncAfterSendTimer);
      this.syncAfterSendTimer = window.setTimeout(() => this.syncActiveMessages(), 350);
    },
    async sendTextToConversation(conversation, text) {
      if (!conversation || !text.trim()) {
        return null;
      }
      const value = text.trim();
      const message = {
        id: randomId(),
        conversationId: conversation.id,
        from: 'me',
        direction: 'out',
        type: 'text',
        text: value,
        status: 'sending',
        deliveryStatus: 'sending',
        timestamp: Date.now(),
        displayTimestamp: Date.now()
      };
      this.appendMessage(conversation.id, message);
      const sentMessage = await imClient.sendTextMessage(conversation, value, message);
      this.replaceMessage(conversation.id, sentMessage);
      return sentMessage;
    },
    upsertConversation(conversation) {
      const index = this.conversations.findIndex(item => item.id === conversation.id);
      if (index >= 0) {
        this.conversations.splice(index, 1, {
          ...this.conversations[index],
          ...conversation
        });
      } else {
        this.conversations.unshift(conversation);
      }
      this.usingDemoData = false;
      writeLocalConversations(this.conversations);
    },
    removeConversation(conversationId) {
      this.conversations = this.conversations.filter(conversation => conversation.id !== conversationId);
      const {
        [conversationId]: removedMessages,
        ...messagesByConversation
      } = this.messagesByConversation;
      const {
        [conversationId]: removedVisibleMessages,
        ...visibleMessagesByConversation
      } = this.visibleMessagesByConversation;
      const {
        [conversationId]: removedOptimisticMessages,
        ...optimisticMessagesByConversation
      } = this.optimisticMessagesByConversation;
      void removedMessages;
      void removedVisibleMessages;
      void removedOptimisticMessages;
      this.messagesByConversation = messagesByConversation;
      this.visibleMessagesByConversation = visibleMessagesByConversation;
      this.optimisticMessagesByConversation = optimisticMessagesByConversation;
      if (this.activeId === conversationId) {
        this.activeId = this.conversations[0]?.id || '';
      }
      writeLocalConversations(this.conversations);
      if (this.activeId) {
        this.loadActiveMessages();
      }
    },
    applyRemoteConversations(remoteConversations) {
      const previousActiveId = this.activeId;
      const remote = Array.isArray(remoteConversations) ? remoteConversations : [];
      if (!remote.length && this.conversations.length) {
        return;
      }

      const merged = new Map();
      this.conversations.forEach(conversation => {
        merged.set(conversation.id, conversation);
      });
      remote.forEach(conversation => {
        const unread = conversation.id === this.activeId && canMarkReadNow() ? 0 : Number(conversation.unread || 0);
        const previous = merged.get(conversation.id) || {};
        const latestRecallDirection = conversation.raw?.latestRecallDirection || '';
        merged.set(conversation.id, {
          ...previous,
          ...conversation,
          previewMessage: latestRecallDirection === 'out' ? null : conversation.previewMessage,
          subtitle: latestRecallDirection === 'out'
            ? String(previous.subtitle || '').replace(/^\[已撤回\]$/, '')
            : conversation.subtitle,
          unread
        });
      });

      this.conversations = Array.from(merged.values())
        .sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0))
        .slice(0, 100);

      if (!this.conversations.length) {
        this.activeId = '';
        this.messagesByConversation = {};
        this.visibleMessagesByConversation = {};
        this.optimisticMessagesByConversation = {};
        writeLocalConversations(this.conversations);
        return;
      }

      if (!this.conversations.find(item => item.id === this.activeId)) {
        this.activeId = this.conversations[0].id;
      }
      writeLocalConversations(this.conversations);
      this.loadActiveMessages();
      if (previousActiveId !== this.activeId) {
        this.refreshActiveGroupMessageActionSettings();
      }
    },
    appendMessage(conversationId, message) {
      if (!this.messagesByConversation[conversationId]) {
        this.messagesByConversation = {
          ...this.messagesByConversation,
          [conversationId]: []
        };
      }
      if (message.direction === 'out' && (message.status === 'sending' || message.status === 'sent')) {
        this.keepOptimisticMessage(conversationId, message);
      }
      if (!hasExactMessage(this.messagesByConversation[conversationId], message)) {
        this.messagesByConversation = {
          ...this.messagesByConversation,
          [conversationId]: [...this.messagesByConversation[conversationId], message]
        };
      }
      this.updateConversationFromMessage(conversationId, message);
      this.syncVisibleMessages(conversationId);
      writeLocalConversations(this.conversations);
    },
    revealMessage(conversationId, message) {
      if (!conversationId || !message) {
        return;
      }
      const current = this.messagesByConversation[conversationId] || [];
      if (!hasExactMessage(current, message)) {
        this.messagesByConversation = {
          ...this.messagesByConversation,
          [conversationId]: [...current, message]
        };
      }
      this.syncVisibleMessages(conversationId);
    },
    patchConversationSettings(conversationId, settings = {}) {
      const conversation = this.conversations.find(item => item.id === conversationId);
      if (!conversation) {
        return;
      }
      Object.assign(conversation, settings);
      conversation.raw = {
        ...(conversation.raw || {}),
        ...settings
      };
      this.conversations = [...this.conversations];
      writeLocalConversations(this.conversations);
    },
    clearConversationMessages(conversationId) {
      if (!conversationId) {
        return;
      }
      this.messagesByConversation = {
        ...this.messagesByConversation,
        [conversationId]: []
      };
      this.visibleMessagesByConversation = {
        ...this.visibleMessagesByConversation,
        [conversationId]: []
      };
      this.optimisticMessagesByConversation = {
        ...this.optimisticMessagesByConversation,
        [conversationId]: []
      };
      this.selectedMessageIds = [];
      this.selectionMode = false;
    },
    removeMessages(conversationId, messages) {
      const ids = new Set(messages.flatMap(message => [message.id, message.messageUid].filter(Boolean).map(String)));
      const filterMessages = list => (list || []).filter(message => {
        return ![message.id, message.messageUid].filter(Boolean).some(id => ids.has(String(id)));
      });
      this.messagesByConversation = {
        ...this.messagesByConversation,
        [conversationId]: filterMessages(this.messagesByConversation[conversationId])
      };
      this.visibleMessagesByConversation = {
        ...this.visibleMessagesByConversation,
        [conversationId]: filterMessages(this.visibleMessagesByConversation[conversationId])
      };
      this.optimisticMessagesByConversation = {
        ...this.optimisticMessagesByConversation,
        [conversationId]: filterMessages(this.optimisticMessagesByConversation[conversationId])
      };
      this.selectedMessageIds = this.selectedMessageIds.filter(id => !ids.has(String(id)));
    },
    replaceLocalMessage(conversationId, targetMessage, nextMessage) {
      const keys = new Set([targetMessage.id, targetMessage.messageUid].filter(Boolean).map(String));
      const replaceInList = list => (list || []).map(message => {
        const matched = [message.id, message.messageUid].filter(Boolean).some(id => keys.has(String(id)));
        return matched ? { ...message, ...nextMessage } : message;
      });
      this.messagesByConversation = {
        ...this.messagesByConversation,
        [conversationId]: replaceInList(this.messagesByConversation[conversationId])
      };
      this.visibleMessagesByConversation = {
        ...this.visibleMessagesByConversation,
        [conversationId]: replaceInList(this.visibleMessagesByConversation[conversationId])
      };
    },
    updateConversationFromMessage(conversationId, message) {
      const conversation = this.conversations.find(item => item.id === conversationId);
      if (!conversation) {
        return;
      }

      conversation.subtitle = message.text || conversation.subtitle || '';
      conversation.previewMessage = previewDescriptor(message);
      conversation.updatedAt = messageActivityTimestamp(message) || Date.now();
      if (message.direction === 'in') {
        conversation.unread = conversationId === this.activeId ? 0 : Number(conversation.unread || 0) + 1;
      }
      this.conversations = [...this.conversations]
        .sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0));
    },
    appendVisibleMessage(conversationId, message) {
      const messages = this.visibleMessagesByConversation[conversationId] || [];
      this.visibleMessagesByConversation = {
        ...this.visibleMessagesByConversation,
        [conversationId]: [...messages.filter(item => item.id !== message.id), message]
          .sort((a, b) => Number(a.displayTimestamp || a.timestamp || 0) - Number(b.displayTimestamp || b.timestamp || 0))
      };
    },
    replaceMessage(conversationId, message) {
      if (!this.messagesByConversation[conversationId]) {
        this.messagesByConversation = {
          ...this.messagesByConversation,
          [conversationId]: []
        };
      }
      if (message.direction === 'out' && (message.status === 'sending' || message.status === 'sent' || message.status === 'failed')) {
        this.keepOptimisticMessage(conversationId, message);
      }
      const index = this.messagesByConversation[conversationId].findIndex(item => item.id === message.id);
      if (index >= 0) {
        const messages = [...this.messagesByConversation[conversationId]];
        messages.splice(index, 1, message);
        this.messagesByConversation = {
          ...this.messagesByConversation,
          [conversationId]: messages
        };
      } else {
        this.messagesByConversation = {
          ...this.messagesByConversation,
          [conversationId]: [...this.messagesByConversation[conversationId], message]
        };
      }
      this.syncVisibleMessages(conversationId);
    },
    syncVisibleMessages(conversationId) {
      this.visibleMessagesByConversation = {
        ...this.visibleMessagesByConversation,
        [conversationId]: mergeVisibleMessages(
          this.optimisticMessagesByConversation[conversationId] || [],
          this.messagesByConversation[conversationId] || []
        )
      };
    },
    keepOptimisticMessage(conversationId, message) {
      if (!this.optimisticMessagesByConversation[conversationId]) {
        this.optimisticMessagesByConversation = {
          ...this.optimisticMessagesByConversation,
          [conversationId]: []
        };
      }
      const messages = this.optimisticMessagesByConversation[conversationId];
      const index = messages.findIndex(item => item.id === message.id);
      let nextMessages;
      if (index >= 0) {
        nextMessages = [...messages];
        nextMessages.splice(index, 1, message);
      } else {
        nextMessages = [...messages, message];
      }
      this.optimisticMessagesByConversation = {
        ...this.optimisticMessagesByConversation,
        [conversationId]: nextMessages
      };
    },
    pruneOptimisticMessages(conversationId, syncedMessages) {
      const messages = this.optimisticMessagesByConversation[conversationId] || [];
      if (!messages.length) {
        return;
      }
      this.optimisticMessagesByConversation = {
        ...this.optimisticMessagesByConversation,
        [conversationId]: messages.filter(message => {
        const remoteMatched = syncedMessages.some(syncedMessage => {
          return syncedMessage.id !== message.id && isSameMessage(syncedMessage, message);
        });
        if (remoteMatched && message.status !== 'failed') {
          return false;
        }
          return Date.now() - Number(message.displayTimestamp || message.timestamp || 0) < 30000 || message.status === 'failed';
        })
      };
    },
    loadActiveMessages(options = {}) {
      const syncOptions = typeof options === 'boolean'
        ? { allowReadReceipt: options }
        : options;
      const conversation = this.activeConversation;
      if (!conversation) {
        return;
      }
      this.loading = true;
      const messages = imClient.getMessages(conversation, 30);
      this.pruneOptimisticMessages(conversation.id, messages);
      this.messagesByConversation = {
        ...this.messagesByConversation,
        [conversation.id]: mergeVisibleMessages(this.messagesByConversation[conversation.id] || [], messages)
      };
      this.syncVisibleMessages(conversation.id);
      this.loading = false;
      this.syncActiveMessages(syncOptions);
    },
    async syncActiveMessages(options = {}) {
      const conversation = this.activeConversation;
      if (!conversation) {
        return;
      }
      const wantsReadReceipt = Boolean(options.allowReadReceipt);
      const explicitReadReceipt = wantsReadReceipt && canMarkReadNow({ userAction: Boolean(options.userAction) });
      const pollingReadReceipt = canMarkReadNow();
      const markRead = explicitReadReceipt || pollingReadReceipt;

      if (this.syncInFlight) {
        if (wantsReadReceipt) {
          this.pendingReadReceiptConversationId = conversation.id;
        }
        return;
      }
      this.syncInFlight = true;
      const syncedConversationId = conversation.id;
      const previousMessages = this.messagesByConversation[syncedConversationId] || [];
      try {
        const messages = await imClient.fetchRemoteMessages(conversation, 50, {
          markRead
        });
        const editedIncomingMessages = incomingEditedMessages(previousMessages, messages);
        this.pruneOptimisticMessages(syncedConversationId, messages);
        this.messagesByConversation = {
          ...this.messagesByConversation,
          [syncedConversationId]: mergeVisibleMessages(this.messagesByConversation[syncedConversationId] || [], messages)
        };
        this.syncVisibleMessages(syncedConversationId);
        if (editedIncomingMessages.length && syncedConversationId === this.activeId) {
          this.focusMessage(editedIncomingMessages.sort((a, b) => messageActivityTimestamp(b) - messageActivityTimestamp(a))[0]);
          playNotificationSound();
        }
        const latest = messages.reduce((latestMessage, message) => {
          if (!latestMessage || messageActivityTimestamp(message) > messageActivityTimestamp(latestMessage)) {
            return message;
          }
          return latestMessage;
        }, null);
        if (latest) {
          conversation.subtitle = messagePreviewText(latest);
          conversation.previewMessage = previewDescriptor(latest);
          conversation.updatedAt = messageActivityTimestamp(latest);
          writeLocalConversations(this.conversations);
        }
      } catch (error) {
        // Keep the local view usable if a polling request misses.
      } finally {
        this.syncInFlight = false;
        if (this.pendingReadReceiptConversationId === this.activeId && canMarkReadNow()) {
          this.pendingReadReceiptConversationId = '';
          window.setTimeout(() => this.syncActiveMessages({ allowReadReceipt: true }), 0);
        }
      }
    },
    startMessageSync() {
      this.bindReadPresenceEvents();
      if (!this.syncTimer) {
        this.syncTimer = window.setInterval(() => {
          this.syncActiveMessages();
        }, Math.max(250, appConfig.syncIntervalMs));
      }
      if (!this.conversationSyncTimer) {
        this.conversationSyncTimer = window.setInterval(() => {
          this.refreshRemoteConversations();
        }, 2000);
        this.refreshRemoteConversations();
      }
      if (!this.messageActionSettingsTimer) {
        this.messageActionSettingsTimer = window.setInterval(() => {
          this.refreshMessageActionSettings();
          this.refreshActiveGroupMessageActionSettings();
        }, 60000);
      }
    },
    bindReadPresenceEvents() {
      if (this.readPresenceEventsBound || typeof window === 'undefined' || typeof document === 'undefined') {
        return;
      }
      this.readPresenceEventsBound = true;
      const syncWhenReadable = () => {
        if (canMarkReadNow()) {
          this.syncActiveMessages({ allowReadReceipt: true });
        }
      };
      window.addEventListener('focus', syncWhenReadable);
      document.addEventListener('visibilitychange', syncWhenReadable);
    },
    async refreshRemoteConversations() {
      this.bindImEvents();
      this.usingDemoData = false;
      if (this.conversationSyncInFlight) {
        return;
      }
      this.conversationSyncInFlight = true;
      try {
        const remoteConversations = await imClient.fetchRemoteConversations(50);
        this.applyRemoteConversations(remoteConversations);
      } catch (error) {
        imClient.refreshConversationList().catch(() => {});
      } finally {
        this.conversationSyncInFlight = false;
      }
    },
    setActionNotice(message) {
      this.actionNotice = message;
      this.actionError = '';
    },
    setActionError(message) {
      this.actionError = message;
      this.actionNotice = '';
    },
    clearActionFeedback() {
      this.actionNotice = '';
      this.actionError = '';
    },
    focusMessage(message) {
      const key = messageStableKey(message);
      if (!key) {
        return;
      }
      this.attentionMessageId = key;
      window.clearTimeout(this.attentionMessageTimer);
      this.attentionMessageTimer = window.setTimeout(() => {
        if (this.attentionMessageId === key) {
          this.attentionMessageId = '';
        }
      }, 2800);
    },
    async handleMessageAction({ action, message }) {
      if (!message) {
        return;
      }
      if (action === 'copy') {
        await this.copyMessages([message]);
      } else if (action === 'edit') {
        this.startEditMessage(message);
      } else if (action === 'delete') {
        this.deleteMessages([message]);
      } else if (action === 'forward') {
        this.startForward([message]);
      } else if (action === 'favorite') {
        this.toggleFavorite(message);
      } else if (action === 'detail') {
        await this.openMessageDetail(message);
      } else if (action === 'quote') {
        this.quoteMessage(message);
      } else if (action === 'select') {
        this.enterSelectionMode(message);
      } else if (action === 'recall') {
        await this.recallMessage(message);
      }
    },
    async openMessageDetail(message) {
      const conversation = this.activeConversation;
      if (!conversation || !message) {
        return;
      }
      const key = messageStableKey(message);
      this.messageDetailOpen = true;
      this.messageDetailLoading = true;
      this.messageDetailErrorKey = '';
      this.messageDetail = {
        messageUid: key,
        conversationType: conversation.type || conversation.raw?.type || 'single',
        sentAt: Number(message.displayTimestamp || message.timestamp || 0),
        editedAt: Number(message.editedAt || 0),
        recalled: isRecalledMessage(message),
        localOnly: true,
        sender: {
          uid: message.from || '',
          name: message.senderName || message.from || ''
        }
      };
      try {
        const detail = await imClient.getMessageDetail(conversation, message);
        this.messageDetail = detail || this.messageDetail;
        if (detail?.localOnly) {
          this.messageDetailErrorKey = 'chat.localDetailUnavailable';
        }
      } catch (error) {
        this.messageDetailErrorKey = error?.code === 'GROUP_RECEIPT_SENDER_ONLY'
          ? 'chat.receiptSenderOnly'
          : 'chat.receiptDetailUnavailable';
      } finally {
        this.messageDetailLoading = false;
      }
    },
    closeMessageDetail() {
      this.messageDetailOpen = false;
      this.messageDetailLoading = false;
      this.messageDetailErrorKey = '';
      this.messageDetail = null;
    },
    async copyMessages(messages) {
      const text = messages.map(message => message.text || '').join('\n');
      if (!text.trim()) {
        return;
      }
      try {
        await writeClipboard(text);
        this.setActionNotice(messages.length > 1 ? '已复制所选消息' : '已复制');
      } catch (error) {
        this.setActionError('复制失败');
      }
    },
    deleteMessages(messages) {
      const conversation = this.activeConversation;
      if (!conversation || !messages.length) {
        return;
      }
      imClient.hideMessages(conversation, messages);
      this.removeMessages(conversation.id, messages);
      if (!this.selectedMessageIds.length) {
        this.selectionMode = false;
      }
      this.setActionNotice(messages.length > 1 ? '已删除所选消息' : '已删除');
    },
    toggleFavorite(message) {
      const key = messageStableKey(message);
      if (!key) {
        return;
      }
      const index = this.favoriteMessages.findIndex(item => item.key === key);
      if (index >= 0) {
        this.favoriteMessages.splice(index, 1);
        if (this.activeFavoriteKey === key) {
          this.activeFavoriteKey = this.favoriteMessages[0]?.key || '';
        }
        this.setActionNotice('已取消收藏');
      } else {
        const conversation = this.conversations.find(item => item.id === message.conversationId) || {};
        this.favoriteMessages.unshift({
          key,
          id: message.id,
          messageUid: message.messageUid,
          conversationId: message.conversationId,
          conversationTitle: conversation.title || message.conversationTitle || '',
          type: message.type || 'text',
          text: message.text || '',
          preview: messagePreviewText(message),
          senderName: message.senderName || message.from || '',
          timestamp: Number(message.displayTimestamp || message.timestamp || Date.now()),
          editedAt: Number(message.editedAt || 0),
          recalled: isRecalledMessage(message),
          recallStatus: message.recallStatus || (isRecalledMessage(message) ? '已撤回' : ''),
          attachment: message.attachment ? { ...message.attachment } : null
        });
        this.activeFavoriteKey = key;
        this.setActionNotice('已收藏');
      }
      this.favoriteMessages = this.favoriteMessages.slice(0, 200);
      writeFavoriteMessages(this.favoriteMessages);
    },
    setActiveFavorite(key) {
      this.activeFavoriteKey = String(key || '');
    },
    removeFavorite(key) {
      const value = String(key || '');
      if (!value) {
        return;
      }
      this.favoriteMessages = this.favoriteMessages.filter(message => message.key !== value);
      if (this.activeFavoriteKey === value) {
        this.activeFavoriteKey = this.favoriteMessages[0]?.key || '';
      }
      writeFavoriteMessages(this.favoriteMessages);
    },
    quoteMessage(message) {
      this.quotedMessage = {
        ...message,
        quoteText: quotePrefix(message)
      };
      this.setActionNotice('已引用，输入后发送');
    },
    cancelQuote() {
      this.quotedMessage = null;
    },
    enterSelectionMode(message = null) {
      this.selectionMode = true;
      if (message) {
        const key = messageStableKey(message);
        this.selectedMessageIds = key ? [key] : [];
      }
    },
    toggleSelectedMessage(message) {
      const key = messageStableKey(message);
      if (!key) {
        return;
      }
      const selected = new Set(this.selectedMessageIds);
      if (selected.has(key)) {
        selected.delete(key);
      } else {
        selected.add(key);
      }
      this.selectedMessageIds = Array.from(selected);
      this.selectionMode = this.selectedMessageIds.length > 0;
    },
    clearSelection() {
      this.selectionMode = false;
      this.selectedMessageIds = [];
    },
    copySelectedMessages() {
      return this.copyMessages(this.selectedMessages);
    },
    deleteSelectedMessages() {
      this.deleteMessages(this.selectedMessages);
    },
    startForward(messages) {
      this.forwardingMessages = messages.filter(message => message?.text);
      if (!this.forwardingMessages.length) {
        this.setActionError('没有可转发的消息');
      }
    },
    startForwardSelectedMessages() {
      this.startForward(this.selectedMessages);
    },
    cancelForward() {
      this.forwardingMessages = [];
    },
    startEditMessage(message) {
      this.clearActionFeedback();
      const isResend = isRecalledMessage(message);
      if (!isResend && isMessageActionExpired(message, this.activeEditLimitMinutes)) {
        this.setActionError('这条消息已超过当前设置的可编辑时间');
        return;
      }
      if (!isResend && !message?.text) {
        this.setActionError('没有可编辑的文字内容');
        return;
      }
      this.editingMessage = message;
      this.editingText = message.text || '';
      this.editingMode = isResend ? 'resend' : 'edit';
      this.editingBusy = false;
      this.editingError = '';
    },
    clearEditFeedback() {
      this.editingError = '';
    },
    cancelEditMessage(force = false) {
      if (this.editingBusy && !force) {
        return;
      }
      this.editingMessage = null;
      this.editingText = '';
      this.editingMode = '';
      this.editingBusy = false;
      this.editingError = '';
    },
    async completeEditMessage() {
      const conversation = this.activeConversation;
      const message = this.editingMessage;
      const text = this.editingText.trim();
      const isResend = this.editingMode === 'resend' || isRecalledMessage(message);
      if (!conversation || !message) {
        this.cancelEditMessage();
        return;
      }
      if (!text) {
        this.editingError = '编辑内容不能为空';
        return;
      }
      if (!isResend && text === String(message.text || '').trim()) {
        this.cancelEditMessage();
        return;
      }
      this.editingBusy = true;
      this.editingError = '';
      try {
        if (isResend) {
          await this.sendTextToConversation(conversation, text);
          this.cancelEditMessage(true);
          this.setActionNotice('已重新发送');
          return;
        }
        const editedMessage = await imClient.editMessage(conversation, message, text);
        this.replaceLocalMessage(conversation.id, message, editedMessage);
        if (messageActivityTimestamp(editedMessage) >= Number(conversation.updatedAt || 0)) {
          conversation.subtitle = editedMessage.text;
          conversation.previewMessage = previewDescriptor(editedMessage);
          conversation.updatedAt = messageActivityTimestamp(editedMessage);
          writeLocalConversations(this.conversations);
        }
        this.cancelEditMessage(true);
        this.setActionNotice('已编辑');
      } catch (error) {
        this.editingError = error.message || '编辑失败';
      } finally {
        this.editingBusy = false;
      }
    },
    async completeForward(targetConversationId) {
      const target = this.conversations.find(conversation => conversation.id === targetConversationId);
      if (!target || !this.forwardingMessages.length) {
        return;
      }
      const text = this.forwardingMessages
        .map(message => `转发：${message.text || ''}`)
        .join('\n\n');
      try {
        await this.sendTextToConversation(target, text);
        this.forwardingMessages = [];
        this.clearSelection();
        this.setActionNotice('已转发');
      } catch (error) {
        this.setActionError(error.message || '转发失败');
      }
    },
    async recallMessage(message) {
      const conversation = this.activeConversation;
      if (!conversation) {
        return;
      }
      if (message.direction === 'out' && isMessageActionExpired(message, this.activeRecallLimitMinutes)) {
        this.setActionError('这条消息已超过当前设置的可撤回时间');
        return;
      }
      try {
        await imClient.recallMessage(conversation, message);
        if (message.direction !== 'out') {
          this.removeMessages(conversation.id, [message]);
          this.setActionNotice('已撤回');
          this.syncActiveMessages();
          return;
        }
        this.replaceLocalMessage(conversation.id, message, {
          status: 'recalled',
          deliveryStatus: '',
          recalled: true,
          recallStatus: '已撤回'
        });
        this.setActionNotice('已撤回');
      } catch (error) {
        this.setActionError(error.message || '撤回失败');
      }
    },
    async createGroup(options = {}) {
      const session = useSessionStore();
      if (!session.userId) {
        this.setActionError('请先登录');
        return false;
      }
      try {
        const conversation = await imClient.createGroup(session.userId, options);
        this.upsertConversation(conversation);
        this.setActiveConversation(conversation.id);
        this.setActionNotice(conversation.welcomeMessageFailed
          ? '群组已创建，但欢迎词发送失败'
          : options.channel ? '公告群已创建' : '群组已创建');
        await this.refreshRemoteConversations();
        return true;
      } catch (error) {
        this.setActionError(error.message || (options.channel ? '创建公告群失败' : '创建群组失败'));
        return false;
      }
    },
    async joinGroup(groupKey) {
      const session = useSessionStore();
      if (!session.userId) {
        this.setActionError('请先登录');
        return false;
      }
      try {
        const conversation = await imClient.joinGroup(session.userId, groupKey);
        this.upsertConversation(conversation);
        this.setActiveConversation(conversation.id);
        this.setActionNotice('已加入群组');
        this.refreshRemoteConversations();
        return true;
      } catch (error) {
        this.setActionError(error.message || '加入群组失败');
        return false;
      }
    },
    async broadcastMessage(targetIds, text) {
      const session = useSessionStore();
      if (!session.userId) {
        this.setActionError('请先登录');
        return false;
      }
      try {
        const result = await imClient.broadcastMessage(session.userId, targetIds, text);
        const successCount = Number(result.successCount || 0);
        const failedCount = Number(result.failedCount || 0);
        this.setActionNotice(failedCount > 0 ? `已群发 ${successCount} 条，失败 ${failedCount} 条` : `已群发 ${successCount} 条`);
        this.refreshRemoteConversations();
        return true;
      } catch (error) {
        this.setActionError(error.message || '群发失败');
        return false;
      }
    },
    async leaveGroup(conversation) {
      const session = useSessionStore();
      if (!session.userId || !conversation?.target) {
        this.setActionError('缺少群组信息');
        return false;
      }
      try {
        await imClient.leaveGroup(session.userId, conversation.target);
        this.removeConversation(conversation.id);
        this.setActionNotice('已退出群组');
        this.refreshRemoteConversations();
        return true;
      } catch (error) {
        this.setActionError(error.message || '退出群组失败');
        return false;
      }
    },
    async dismissGroup(conversation) {
      const session = useSessionStore();
      if (!session.userId || !conversation?.target) {
        this.setActionError('缺少群组信息');
        return false;
      }
      try {
        await imClient.dismissGroup(session.userId, conversation.target);
        this.removeConversation(conversation.id);
        this.setActionNotice('群组已解散');
        this.refreshRemoteConversations();
        return true;
      } catch (error) {
        this.setActionError(error.message || '解散群组失败');
        return false;
      }
    }
  }
});
